import { isWebPSupported } from '@/src/utils/tools'
import { useMemoizedFn } from 'ahooks'
import { ImageProps, default as NextImage } from 'next/image'
import { memo, useEffect, useMemo, useState } from 'react'

import getConfig from 'next/config'

const { publicRuntimeConfig } = getConfig()

const assetPrefix = publicRuntimeConfig.assetPrefix || ''
interface CustomImageProps extends Omit<ImageProps, 'src'> {
  src: string
  errorSrc?: string
  sourceType?: string
}

/**
 *@Description: 扩展next/image组件，使用本地图片时，自动拼接assetPrefix前缀，当浏览器不支持webp时，会自动转换为png格式
 *@param {props} 正常传递Image原生属性
 *@param {props: string} errorSrc 额外拓展了图片加载出错时，兜底的图片src
 *@param {props: string} sourceType 不支持webp时回退的图片类型
 */
function CustomImage({ src, errorSrc, sourceType, alt, ...props }: CustomImageProps) {
  const [imgSrc, setImgSrc] = useState(src)
  const [isError, setIsError] = useState(false)
  const [supportsWebp, setSupportsWebp] = useState(false)
  // 直接从 context 中解构获取 assetPrefix，避免订阅整个 context 的变化
  // console.log('render')
  // 从图片路径中提取文件名作为默认的 alt 文本
  const getDefaultAlt = useMemoizedFn((path: string) => {
    const fileName = path.split('/').pop()?.split('?')[0] || ''
    const nameWithoutExt = fileName.substring(0, fileName.lastIndexOf('.')) || fileName
    return nameWithoutExt.replace(/[-_]/g, ' ')
  })

  useEffect(() => {
    setImgSrc(src)
  }, [src])

  useEffect(() => {
    isWebPSupported().then((supported) => {
      setSupportsWebp(supported as boolean)
    })
  }, [])

  const handleError = useMemoizedFn(() => {
    if (!isError && errorSrc) {
      setIsError(true)
      setImgSrc(errorSrc)
    }
  })
  const finalSrc = useMemo(() => {
    if (imgSrc?.startsWith('http') || imgSrc?.startsWith('//')) {
      return imgSrc
    }
    if (supportsWebp) {
      return `${assetPrefix}${imgSrc}`
    } else if (sourceType === 'png') {
      return `${assetPrefix}${imgSrc.replace('.webp', '.png')}`
    } else {
      return `${assetPrefix}${imgSrc}`
    }
  }, [assetPrefix, imgSrc, supportsWebp, sourceType])
  return <NextImage {...props} alt={alt || getDefaultAlt(imgSrc)} src={finalSrc} onError={handleError} />
}

// 使用 memo 时添加比较函数，确保只有关键属性变化时才重新渲染
export default memo(CustomImage)
